import React, { useState, useEffect, useCallback, useRef } from 'react';
import PricingIssues    from './pages/PricingIssues';
import SerialMissing    from './pages/SerialMissing';
import SerialCostStatus from './pages/SerialCostStatus';
import SerialStockMismatch from './pages/SerialStockMismatch';
import DuePayment       from './pages/DuePayment';
import DayBook          from './pages/DayBook';
import Ledger           from './pages/Ledger';
import Login            from './pages/Login';
import './App.css';

const API = process.env.REACT_APP_API_URL || '';

function getUrlParam(key) {
  return new URLSearchParams(window.location.search).get(key) || '';
}
function setUrlParams(params) {
  const sp = new URLSearchParams(window.location.search);
  Object.entries(params).forEach(([k, v]) => {
    if (v && v !== 'ALL' && v !== '') sp.set(k, v);
    else sp.delete(k);
  });
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('bc_theme') || 'light');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('bc_theme', theme);
  }, [theme]);
  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  return [theme, toggle];
}

const TABS = [
  { id:'pricing',  label:'Pricing Issues', icon:'₹'  },
  { id:'serial',   label:'Serial Missing', icon:'#'  },
  { id:'cost',         label:'Cost Status',    icon:'⚠'  },
  { id:'stockmismatch',label:'Stock Mismatch', icon:'⚡' },
  { id:'due',      label:'Due Payments',   icon:'⏳' },
  { id:'daybook',  label:'Day Book',       icon:'📒' },
  { id:'ledger',   label:'Ledger',         icon:'📊' },
];

export default function App() {
  const [tab,      setTab]      = useState(() => getUrlParam('tab') || 'pricing');
  const [token,    setToken]    = useState(() => localStorage.getItem('bc_token') || '');
  const [username, setUsername] = useState(() => localStorage.getItem('bc_username') || '');
  const [checking, setChecking] = useState(true);
  const [theme, toggleTheme]   = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [databases,  setDatabases]  = useState([]);
  const [selectedDB, setSelectedDB] = useState(() => getUrlParam('db') || localStorage.getItem('bc_db') || '');
  const [dbLoading,  setDbLoading]  = useState(false);

  const drawerRef = useRef(null);

  // Close drawer on outside tap
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e) { if (e.key === 'Escape') setDrawerOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
    setDrawerOpen(false);
    setUrlParams({ tab: newTab });
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    fetch(`${API}/api/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(j => setUsername(j.username))
      .catch(() => {
        localStorage.removeItem('bc_token');
        localStorage.removeItem('bc_username');
        setToken('');
      })
      .finally(() => setChecking(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    setDbLoading(true);
    fetch(`${API}/api/databases`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        const dbs = j.databases || [];
        setDatabases(dbs);
        if (dbs.length > 0) {
          const urlDb = getUrlParam('db');
          const saved = urlDb || localStorage.getItem('bc_db');
          const valid = saved && dbs.find(d => d.name === saved);
          const pick  = valid ? saved : dbs[0].name;
          setSelectedDB(pick);
          localStorage.setItem('bc_db', pick);
          setUrlParams({ db: pick, tab });
        }
      })
      .catch(() => {})
      .finally(() => setDbLoading(false));
  }, [token]); // eslint-disable-line

  function handleDBChange(e) {
    const db = e.target.value;
    setSelectedDB(db);
    localStorage.setItem('bc_db', db);
    setUrlParams({ db });
  }

  function handleLogin(tok, user) { setToken(tok); setUsername(user); }

  function logout() {
    localStorage.removeItem('bc_token');
    localStorage.removeItem('bc_username');
    setToken(''); setUsername('');
    setDrawerOpen(false);
  }

  if (checking) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh',
      flexDirection:'column', gap:14, color:'var(--text3)', background:'var(--bg)' }}>
      <div className="spinner" style={{width:22,height:22}} />
      <span style={{ fontFamily:'var(--mono)', fontSize:12 }}>Verifying session…</span>
    </div>
  );

  if (!token) return <Login onLogin={handleLogin} />;

  const activeTab = TABS.find(t => t.id === tab);

  return (
    <div className="app">

      {/* ── SIDEBAR DRAWER (mobile) ─────────────────────── */}
      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}
      <aside className={`sidebar-drawer ${drawerOpen ? 'drawer-open' : ''}`} ref={drawerRef}>
        {/* Drawer header */}
        <div className="drawer-head">
          <div className="drawer-brand">
            <div className="logo-mark">BC</div>
            <div>
              <div className="brand-name">BusyComp</div>
              <div className="brand-sub">Issues Monitor</div>
            </div>
          </div>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>

        {/* DB selector in drawer */}
        <div className="drawer-db-section">
          <div className="drawer-section-label">Financial Year</div>
          {dbLoading ? (
            <div className="spinner" style={{width:14,height:14}} />
          ) : databases.length > 0 ? (
            <select className="db-selector drawer-db-select" value={selectedDB} onChange={handleDBChange}>
              {databases.map(db => (
                <option key={db.name} value={db.name}>{db.label}</option>
              ))}
            </select>
          ) : (
            <span className="db-badge" style={{color:'var(--amber)',borderColor:'var(--amber-bd)',background:'var(--amber-bg)'}}>
              No DBs found
            </span>
          )}
        </div>

        {/* Nav links */}
        <nav className="drawer-nav">
          <div className="drawer-section-label">Navigation</div>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`drawer-nav-item ${tab === t.id ? 'active' : ''}`}
              onClick={() => handleTabChange(t.id)}>
              <span className="drawer-nav-icon">{t.icon}</span>
              <span className="drawer-nav-label">{t.label}</span>
              {tab === t.id && <span className="drawer-nav-dot" />}
            </button>
          ))}
        </nav>

        {/* User + logout at bottom */}
        <div className="drawer-footer">
          <div className="drawer-user">
            <span className="user-dot" />
            <span className="drawer-username">{username}</span>
          </div>
          <button className="drawer-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      {/* ── HEADER ──────────────────────────────────────── */}
      <header className="app-header">
        <div className="hdr-row1">

          {/* Hamburger — mobile only */}
          <button className="btn-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Menu">
            <span /><span /><span />
          </button>

          <div className="header-brand">
            <div className="logo-mark">BC</div>
            <div>
              <div className="brand-name">BusyComp</div>
              <div className="brand-sub">Issues Monitor</div>
            </div>
          </div>

          <div className="hdr-row1-right">
            {/* DB Selector */}
            <div className="db-selector-wrap">
              <span className="db-selector-label">FY</span>
              {dbLoading ? (
                <div className="spinner" style={{width:14,height:14}} />
              ) : databases.length > 0 ? (
                <select className="db-selector" value={selectedDB} onChange={handleDBChange}>
                  {databases.map(db => (
                    <option key={db.name} value={db.name}>{db.label}</option>
                  ))}
                </select>
              ) : (
                <span className="db-badge" style={{color:'var(--amber)',borderColor:'var(--amber-bd)',background:'var(--amber-bg)'}}>
                  No DBs
                </span>
              )}
            </div>

            <button className="btn-theme" onClick={toggleTheme}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            <div className="user-pill">
              <span className="user-dot" />
              {username}
            </div>

            <button className="btn-logout" onClick={logout}>Logout</button>
          </div>
        </div>

        {/* Tab nav — desktop only, hidden on mobile */}
        <nav className="tab-nav">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => handleTabChange(t.id)}>
              <span className="tab-icon">{t.icon}</span>
              <span className="tab-label">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Mobile active tab indicator bar */}
        <div className="mobile-tab-bar">
          <span className="mobile-tab-icon">{activeTab?.icon}</span>
          <span className="mobile-tab-name">{activeTab?.label}</span>
        </div>
      </header>

      <main className="app-main">
        {!selectedDB ? (
          <div className="no-db-banner fade-up">
            <h3>No financial year selected</h3>
            <p>
              No matching databases found on the server.<br />
              Check <code>DB_BASE</code> in your <code>.env</code> file.<br />
              Expected format: <code>BusyComp0004_db12025</code>
            </p>
          </div>
        ) : (
          <>
            {tab === 'pricing' && <PricingIssues    db={selectedDB} />}
            {tab === 'serial'  && <SerialMissing    db={selectedDB} />}
            {tab === 'cost'         && <SerialCostStatus db={selectedDB} />}
            {tab === 'stockmismatch'&& <SerialStockMismatch db={selectedDB} />}
            {tab === 'due'     && <DuePayment        db={selectedDB} />}
            {tab === 'daybook' && <DayBook           db={selectedDB} />}
            {tab === 'ledger'  && <Ledger            db={selectedDB} />}
          </>
        )}
      </main>
    </div>
  );
}
